import { Component, OnInit, ViewChild } from "@angular/core";
import { MatPaginator, PageEvent } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import { ActivatedRoute, Router } from "@angular/router";
import { BreakpointObserver, Breakpoints } from "@angular/cdk/layout";
import { AccountTransactionService } from "../account-transaction.service";
import { LoanData } from "../../../loan/domain/models/loan.model";
import { AccountTransactionFilterService } from "../account-transaction-filter.service";
import { AccountTransaction, AccountTransactionQueryResult, FailureReasonBaseData, CommonActionKey, SimpleDialogService, TransactionStatus, TransactionStatusChips, TransactionType } from "common";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { LoanFacade } from "../../../loan/domain/+state/loan.facade";
import { FailureReasonService } from "../failure-reason.service";

@UntilDestroy()
@Component({
    selector: "ifp-account-transaction-table",
    templateUrl: "./account-transaction-table.component.html",
    styleUrls: ["./account-transaction-table.component.scss"],
    standalone: false
})
export class AccountTransactionTableComponent implements OnInit {
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  readonly TransactionStatus = TransactionStatus;
  readonly Status = TransactionStatusChips;
  failureReasons: FailureReasonBaseData[];
  get pageSize(): number {
    return this.accountTransactionService.pageSize;
  }

  data: AccountTransactionTableComponentData;
  hidePageSize: boolean;

  displayedColumns: string[] = [
    "type-icon",
    "date",
    "amount",
    "status",
    "initiator",
    "type",
    "notes",
    "reasons",
    "closingBalance",
    "totalPayback",
    "actions",
  ];

  constructor(
    private readonly accountTransactionService: AccountTransactionService,
    private readonly _accountTransactionFilterService: AccountTransactionFilterService,
    private readonly loanFacade: LoanFacade,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly simpleDialogService: SimpleDialogService,
    private readonly failureReasonService: FailureReasonService,
    breakpointObserver: BreakpointObserver,
  ) {
    breakpointObserver.observe([Breakpoints.XSmall]).subscribe((result) => {
      if (result.matches) this.hidePageSize = true;
      else this.hidePageSize = false;
    });
  }

  ngOnInit(): void {
    this.route.data
      .pipe(untilDestroyed(this))
      .subscribe((res: AccountTransactionTableComponentData) => {
        this.data = res;
      });

    this.failureReasonService.list() .pipe(untilDestroyed(this))
      .subscribe({
        next: (failureReasons: FailureReasonBaseData[]) => {
          this.failureReasons = failureReasons;
        }
      });
  }

  sortData(): void {
    this.filter();
  }

  setPage(event: PageEvent): void {
    this.accountTransactionService.pageSize = event.pageSize;
    this.filter();
  }

  filter(): void {
    this._accountTransactionFilterService
      .filter(this.data.loan.id, this.paginator, this.sort)
      .subscribe(
        (result) => (this.data.accountTransactionQueryResult = result)
      );
  }

  transactionDetailLink(txId: string): void {
    const path = `account/${this.data.loan.id}/transaction/${txId}`;
    this.router.navigate([path]);
  }

  transactionCancel(transaction: AccountTransaction): void {
    if (!transaction.cancelable) return;

    this.simpleDialogService
      .openSimpleConfirmDialog('Are you sure you want to cancel this transaction?', 'Cancel Transaction?')
      .subscribe((result: CommonActionKey) => {
        if (result === CommonActionKey.Confirm) {
        this.accountTransactionService.cancelTransaction(transaction.id).subscribe((e) => {
            this.loanFacade.refreshLoan(this.data.loan.id).subscribe()
            this.filter();    
          });
        }
      });
  }

  getTransactionPrimaryAmount(transaction: AccountTransaction): number {
    return (transaction.type === TransactionType.Payment || transaction.type == TransactionType.Reversal || transaction.type == TransactionType.BalanceAdjustment)
      ? transaction.principalAmount
      : transaction.disbursedAmount;
  }

  getTransactionSecondaryAmount(transaction: AccountTransaction): number {
    return (transaction.type === TransactionType.Payment || transaction.type == TransactionType.Reversal || transaction.type == TransactionType.BalanceAdjustment)
      ? transaction.interestAmount
      : transaction.deferredFeeAmount;
  }

  getAvatarIcon(type: number): "D" | "P" | "B" | "F" | "$" | "R" | "A" {
    switch (type) {
      case TransactionType.Draw:
        return "D";
      case TransactionType.Payment:
        return "P";
      case TransactionType.BalanceTransfer:
        return "B";
      case TransactionType.Fee:
        return "F";
      case TransactionType.Premium:
        return "$";
      case TransactionType.Reversal:
        return "R";
      case TransactionType.BalanceAdjustment:
        return "A";
    }
  }

  downloadCsv(): void {
    this.accountTransactionService.downloadCsv(this.data.loan.id);
  }

  filterFunction(failureReason: FailureReasonBaseData, failureReasonCodes: string[]): boolean {
    return failureReasonCodes.includes(failureReason.code);
  }

  selectFunction(failureReason: FailureReasonBaseData[]): string {
    return failureReason?.map(fr => fr.name).join(', ') ?? '';
  }
}

export interface AccountTransactionTableComponentData {
  accountTransactionQueryResult: AccountTransactionQueryResult;
  loan: LoanData;
}
